import { Box } from "@mui/material";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { CoverageTier, resolveCoverageTier } from "../../../../components/UIToolkit/Coverage/CoverageDeltaCell";
import { NameChip } from "../../../../components/UIToolkit/Coverage/NameChip";
import { COVERAGE, GRID_FONT, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import { firstNameLastInitial } from "../../../../utils/strings";
import { EXPANDED_ROW_MIN_H } from "../gridGeometry";
import type { CoverageRow } from "../types";
import { useCoverageGridContext } from "./CoverageGridContext";

type Props = {
	cell: CoverageRow["days"][number];
	weekIdx: number;
	ariaLabel: string;
	dataTestId: string;
	onOpen: () => void;
};

/**
 * One day of a row rendered in-place as read-only assigned name-chips — the per-row
 * "detail" reveal on the Coverage · Summary grid. Clicking the cell still opens the
 * day popover (edit entry point); the chips themselves are not draggable here.
 *
 * Each chip is one-per-line (full column width) and drops the role code — every name
 * in the row shares the row's role, so it's pure noise here and steals width on small
 * screens. Names abbreviate to "First L."; the full name + role live in the tooltip.
 *
 * Linked hover is CSS-driven: each chip stamps `linkedHoverGroupId` and reports
 * enter/leave into `setHoveredNurse` (from context), which flips DOM attributes —
 * no React render.
 */
export const ExpandedCoverageCell = memo(({ cell, weekIdx, ariaLabel, dataTestId, onOpen }: Props) => {
	const { t } = useTranslation();
	const { setHoveredNurse } = useCoverageGridContext();
	const tier = resolveCoverageTier(cell);
	// Dedupe by assignment id — the summary roll-up can list the same assignment twice.
	const names = useMemo(() => Array.from(new Map(cell.names.map((n) => [n.id, n])).values()), [cell.names]);
	const isNotRequired = tier === CoverageTier.NotRequired && names.length === 0;
	return (
		<Box
			className="ms3-cell"
			role="gridcell"
			// Keyboard parity with the collapsed CoverageDeltaCell (a real button): the
			// expanded cell is a focusable gridcell that opens the same day popover on
			// Enter/Space, so keyboard users never lose the edit entry point on expand.
			tabIndex={0}
			data-week={weekIdx}
			data-testid={dataTestId}
			aria-label={ariaLabel}
			onClick={onOpen}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onOpen();
				}
			}}
			sx={(theme) => ({
				display: "flex",
				flexWrap: "wrap",
				alignContent: "flex-start",
				gap: 0.375,
				p: 0.5,
				minHeight: EXPANDED_ROW_MIN_H,
				width: "100%",
				cursor: "pointer",
				outline: "none",
				backgroundColor: isNotRequired ? COVERAGE.MUTED_BG : SURFACE,
				// Match CoverageDeltaCell's focus ring — inset so it stays visible in the 1px lattice.
				"&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: "-2px", zIndex: 1 },
			})}
		>
			{names.map((n) => (
				<InHouseTooltip key={n.id} title={n.role ? `${n.name} · ${n.role}` : n.name}>
					<NameChip
						name={firstNameLastInitial(n.name)}
						charge={n.charge}
						chargeLabel={n.charge ? t("schedule.template.v3.detail.charge") : undefined}
						ot={n.ot}
						otLabel={n.ot ? t("schedule.template.v3.people.otBadge") : undefined}
						onPto={n.onPto}
						ariaLabel={n.name}
						linkedHoverGroupId={n.nurseId}
						onHoverChange={(hovered) => setHoveredNurse(hovered ? n.nurseId : null)}
						sx={{ width: "100%" }}
					/>
				</InHouseTooltip>
			))}
			{names.length === 0 && (
				<Box
					sx={{
						width: "100%",
						minHeight: `calc(${EXPANDED_ROW_MIN_H} - 8px)`,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						color: NEUTRAL[400],
						fontSize: GRID_FONT.MD,
					}}
				>
					·
				</Box>
			)}
		</Box>
	);
});
